// src/modules/communications/services/communicationService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Get the auth token from localStorage
const getAuthToken = () => localStorage.getItem('token');

// Set the Authorization header with the token
const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`
  }
});

// Get all communications with filtering options
export const getAllCommunications = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add all filters to query parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const response = await axios.get(
      `${API_URL}/communications/all?${queryParams.toString()}`, 
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching communications:', error);
    throw error;
  }
};

// Get communications dashboard statistics
export const getCommunicationsStats = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/stats`, 
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching communications stats:', error);
    throw error;
  }
};

// Get placeholder leads (unknown callers)
export const getPlaceholderLeads = async (page = 1, limit = 20) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/placeholder-leads`,
      {
        ...getHeaders(),
        params: { page, limit }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching placeholder leads:', error);
    throw error;
  }
};

// Get communications for a specific placeholder lead
export const getPlaceholderLeadCommunications = async (leadId) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/placeholder-leads/${leadId}/communications`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching placeholder lead communications:', error);
    throw error;
  }
};

// Upgrade a placeholder lead to a real lead
export const upgradePlaceholderLead = async (leadId, leadData) => {
  try {
    const response = await axios.put(
      `${API_URL}/communications/upgrade-placeholder-lead/${leadId}`,
      leadData,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error upgrading placeholder lead:', error);
    throw error;
  }
};

// Transcribe a call recording
export const transcribeCall = async (callId) => {
  try {
    const response = await axios.post(
      `${API_URL}/communications/calls/${callId}/transcribe`,
      {},
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error transcribing call:', error);
    throw error;
  }
};

// Get transcription for a call
export const getTranscription = async (callId) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/calls/${callId}/transcription`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching transcription:', error);
    throw error;
  }
};

// Send an SMS/MMS message
export const sendSms = async (toNumber, body, leadId, mediaFile = null) => {
  try {
    let response;
    if (mediaFile) {
      const formData = new FormData();
      formData.append('to_number', toNumber);
      formData.append('body', body);
      if (leadId) formData.append('lead_id', leadId);
      formData.append('media', mediaFile);
      response = await axios.post(
        `${API_URL}/communications/sms`,
        formData,
        { headers: { ...getHeaders().headers, 'Content-Type': 'multipart/form-data' } }
      );
    } else {
      response = await axios.post(
        `${API_URL}/communications/sms`,
        { to_number: toNumber, body, lead_id: leadId },
        getHeaders()
      );
    }
    return response.data;
  } catch (error) {
    console.error('Error sending SMS:', error);
    throw error;
  }
};

// Initiate a click-to-call
export const initiateClickToCall = async (callData) => {
  try {
    const response = await axios.post(
      `${API_URL}/communications/click-to-call`,
      callData,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error initiating click-to-call:', error);
    throw error;
  }
};

// Get communication history for a lead
export const getLeadCommunicationHistory = async (leadId) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/leads/${leadId}/history`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching lead communication history:', error);
    throw error;
  }
};